import { Phone, Mail, MapPin, Clock, MessageSquareText, ShieldCheck, Languages } from "lucide-react";
import { usePageTitle } from "@/hooks/usePageTitle";
import PageHero from "@/components/PageHero";
import TourForm from "@/components/TourForm";
import { Reveal } from "@/components/Reveal";
import { Ladybug, WateringCan, FlowerDoodle } from "@/components/Doodles";
import { useLang } from "../lang/LanguageContext";

export default function Contact() {
    usePageTitle("Schedule a Daycare Tour | Little Seeds Flourish Massapequa Park");
    const { lang, t } = useLang();

    return (
        <div>
            <PageHero
                eyebrow={t("Schedule a tour")}
                title={lang === "es" ? "¡Ven a saludarnos! Nos encantaría conocer a tu familia." : <>Come Say Hello! We'd Love to Meet Your Family.</>}
                sub={t("Tell us a little about your family and Asia will reach out within 24 hours to set up your visit.")}
                doodle="sun"
            />

            <section className="pb-20 md:pb-28">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1.5fr_1fr] gap-8 items-start">
                    <Reveal>
                        <TourForm />
                    </Reveal>

                    <div className="space-y-5 lg:sticky lg:top-28">
                        <Reveal delay={0.08}>
                            <div className="bg-creamsoft border-2 border-soil/10 rounded-[2rem] p-7" data-testid="contact-details-card">
                                <h2 className="font-display font-semibold text-2xl mb-5">{t("Reach us directly")}</h2>
                                <ul className="space-y-4">
                                    <li>
                                        <a
                                            href="tel:+15163243418"
                                            data-testid="contact-call-button"
                                            className="flex items-center gap-3.5 bg-sage hover:bg-sagedeep text-white rounded-2xl px-4 py-3.5 font-extrabold transition-colors"
                                        >
                                            <span className="inline-flex p-2 rounded-xl bg-white/20"><Phone size={18} /></span>
                                            {t("Call 516-324-3418")}
                                        </a>
                                    </li>
                                    <li>
                                        <a
                                            href="sms:+15163243418"
                                            data-testid="contact-text-us-button"
                                            className="flex items-center gap-3.5 bg-white border-2 border-sage/40 hover:border-sage text-soil rounded-2xl px-4 py-3.5 font-extrabold transition-colors"
                                        >
                                            <span className="inline-flex p-2 rounded-xl bg-sage/15"><MessageSquareText size={18} className="text-sageink" /></span>
                                            {t("Text 516-324-3418")}
                                        </a>
                                    </li>
                                    <li>
                                        <a
                                            href="mailto:littleseedsflourish@gmail.com"
                                            data-testid="contact-email-button"
                                            className="flex items-center gap-3.5 bg-white border-2 border-soil/10 rounded-2xl px-4 py-3.5 font-bold hover:border-sage/50 transition-colors"
                                        >
                                            <span className="inline-flex p-2 rounded-xl bg-sun/25"><Mail size={18} className="text-soil" /></span>
                                            littleseedsflourish@gmail.com
                                        </a>
                                    </li>
                                </ul>
                                <p className="mt-4 text-sm font-bold text-soil/75 flex items-center gap-2" data-testid="contact-spanish-line">
                                    <Languages size={16} className="text-coraldeep shrink-0" />
                                    Hablo español. ¡Llámeme o envíeme un mensaje!
                                </p>
                                <div className="mt-5 pt-5 border-t-2 border-soil/10 space-y-3 text-soil font-semibold">
                                    <p className="flex items-center gap-3"><MapPin size={18} className="text-coraldeep shrink-0" /> Massapequa Park, NY</p>
                                    <p className="flex items-center gap-3"><Clock size={18} className="text-coraldeep shrink-0" /> <span>{t("Mon–Fri 6:00 AM – 8:00 PM")}<br />{t("Sat 9:00 AM – 6:00 PM")}</span></p>
                                </div>
                            </div>
                        </Reveal>

                        <Reveal delay={0.14}>
                            <div className="bg-skysoft border-2 border-sky rounded-[2rem] p-6 flex gap-4 items-start" data-testid="contact-privacy-card">
                                <ShieldCheck size={22} className="text-sageink shrink-0 mt-1" />
                                <p className="text-sm text-soil leading-relaxed">
                                    {t("We show the town only — no street address or map pin until a tour is booked. That's common for home daycares, and it keeps our family and yours safe.")}
                                </p>
                            </div>
                        </Reveal>

                        <Reveal delay={0.2}>
                            <div className="relative rounded-[2rem] overflow-hidden" aria-hidden="true">
                                <img
                                    src="/images/art-animals.jpg"
                                    alt=""
                                    className="w-full rounded-[2rem] border-4 border-white shadow"
                                />
                                <Ladybug className="absolute top-3 right-4" />
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>
        </div>
    );
}
